import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useNotify } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Deal record page (Stage 2a) — CAPTURE underwriting/offer/contract as ENTERED
// data (D25 track-only; not computed). Advance the configurable stages; Lost
// recycles the lead to ME. Nothing auto-sends to a seller.

type Stage = { id: string; key: string; name: string; position: number; is_terminal: boolean };
const OFFER_STATUS = ["", "drafted", "presented", "countered", "accepted", "declined"];
const CONTRACT_STATUS = ["", "none", "draft", "sent", "signed", "void"];

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const DealShow = () => {
  const { id } = useParams();
  const notify = useNotify();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<any>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const sb = getSupabaseClient();
    const [{ data: d }, { data: st }, { data: tk }] = await Promise.all([
      sb.from("deals_summary").select("*").eq("id", id).maybeSingle(),
      sb.from("deal_stages").select("*").order("position"),
      sb.from("tasks").select("id,name,priority,status").eq("deal_id", id).order("created_at", { ascending: false }),
    ]);
    setDeal(d); setStages((st as Stage[]) ?? []); setTasks(tk ?? []);
  };
  useEffect(() => { load(); }, [id]);

  if (!deal) return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;

  const set = (k: string, v: any) => setDeal((d: any) => ({ ...d, [k]: v }));
  const num = (v: any) => (v === "" || v == null ? null : Number(v));

  const save = async () => {
    setSaving(true);
    const patch = {
      arv: num(deal.arv), rehab_estimate: num(deal.rehab_estimate), mao: num(deal.mao),
      expected_profit: num(deal.expected_profit), underwriting_notes: deal.underwriting_notes, attachment_url: deal.attachment_url || null,
      offer_price: num(deal.offer_price), offer_date: deal.offer_date || null, offer_status: deal.offer_status || null, offer_notes: deal.offer_notes,
      contract_status: deal.contract_status || null, docusign_ref: deal.docusign_ref || null,
      contract_date: deal.contract_date || null, close_date: deal.close_date || null,
    };
    const { error } = await getSupabaseClient().from("deals").update(patch).eq("id", id);
    setSaving(false);
    notify(error ? `Save failed: ${error.message}` : "Deal saved", { type: error ? "error" : "success" });
    if (!error) load();
  };

  const changeStage = async (stageId: string) => {
    const { error } = await getSupabaseClient().from("deals").update({ deal_stage_id: stageId }).eq("id", id);
    if (error) { notify(`Stage change failed: ${error.message}`, { type: "error" }); return; }
    notify("Stage updated — S&A tasks may have been created", { type: "success" });
    load();
  };

  const markLost = async () => {
    const reason = window.prompt("Reason for marking this deal Lost? (the lead recycles back to marketing)");
    if (reason == null) return;
    const { error } = await getSupabaseClient().rpc("set_deal_lost", { p_deal_id: id, p_reason: reason });
    if (error) { notify(`Failed: ${error.message}`, { type: "error" }); return; }
    notify("Deal marked Lost — lead recycled to the Marketing Engine", { type: "success" });
    navigate("/deals");
  };

  return (
    <div className="p-4 max-w-4xl">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div>
          <h1 className="text-xl font-semibold">{deal.contact_name || "(deal)"}</h1>
          <div className="text-sm text-muted-foreground">
            {[deal.address_line1, deal.city, deal.county].filter(Boolean).join(", ") || "—"}
            {deal.lead_id && <> · <Link className="hover:underline" to={`/leads/${deal.lead_id}/show`}>lead</Link></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!deal.active && <Badge variant="outline">inactive · {deal.stage_name}</Badge>}
          {deal.active && (
            <select className="border rounded px-2 py-1 text-sm bg-background" value={deal.deal_stage_id} onChange={(e) => changeStage(e.target.value)}>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Disposition-handoff signal (Stage 2b) — flag only; Transaction Management is Stage 4. */}
      {deal.disposition_ready && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/40 px-3 py-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-green-700 dark:text-green-400" />
          <span className="text-green-800 dark:text-green-300">
            Handoff to disposition sent{deal.disposition_handoff_at ? ` on ${new Date(deal.disposition_handoff_at).toLocaleDateString()}` : ""} —
            {deal.disposition_consumed_at ? " picked up by Transaction Management." : " queued for Transaction Management (Stage 4)."}
          </span>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Underwriting <span className="text-xs font-normal text-muted-foreground">(entered)</span></CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Field label="ARV"><Input type="number" value={deal.arv ?? ""} onChange={(e) => set("arv", e.target.value)} /></Field>
            <Field label="Rehab estimate"><Input type="number" value={deal.rehab_estimate ?? ""} onChange={(e) => set("rehab_estimate", e.target.value)} /></Field>
            <Field label="MAO"><Input type="number" value={deal.mao ?? ""} onChange={(e) => set("mao", e.target.value)} /></Field>
            <Field label="Expected profit"><Input type="number" value={deal.expected_profit ?? ""} onChange={(e) => set("expected_profit", e.target.value)} /></Field>
            <div className="col-span-2"><Field label="Notes"><Textarea rows={2} value={deal.underwriting_notes ?? ""} onChange={(e) => set("underwriting_notes", e.target.value)} /></Field></div>
            <div className="col-span-2"><Field label="Attachment link (memo produced outside the CRM)"><Input value={deal.attachment_url ?? ""} onChange={(e) => set("attachment_url", e.target.value)} placeholder="https://…" /></Field></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Offer <span className="text-xs font-normal text-muted-foreground">(recorded · approval is external)</span></CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Field label="Offer price"><Input type="number" value={deal.offer_price ?? ""} onChange={(e) => set("offer_price", e.target.value)} /></Field>
            <Field label="Offer date"><Input type="date" value={deal.offer_date ?? ""} onChange={(e) => set("offer_date", e.target.value)} /></Field>
            <Field label="Offer status">
              <select className="border rounded px-2 py-2 text-sm bg-background w-full" value={deal.offer_status ?? ""} onChange={(e) => set("offer_status", e.target.value)}>
                {OFFER_STATUS.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
              </select>
            </Field>
            <div className="col-span-2"><Field label="Offer notes"><Textarea rows={2} value={deal.offer_notes ?? ""} onChange={(e) => set("offer_notes", e.target.value)} /></Field></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Contract <span className="text-xs font-normal text-muted-foreground">(tracked status)</span></CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <Field label="Contract status">
              <select className="border rounded px-2 py-2 text-sm bg-background w-full" value={deal.contract_status ?? ""} onChange={(e) => set("contract_status", e.target.value)}>
                {CONTRACT_STATUS.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
              </select>
            </Field>
            <Field label="DocuSign ref / link"><Input value={deal.docusign_ref ?? ""} onChange={(e) => set("docusign_ref", e.target.value)} /></Field>
            <Field label="Contract date"><Input type="date" value={deal.contract_date ?? ""} onChange={(e) => set("contract_date", e.target.value)} /></Field>
            <Field label="Close date"><Input type="date" value={deal.close_date ?? ""} onChange={(e) => set("close_date", e.target.value)} /></Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Tasks</CardTitle></CardHeader>
          <CardContent>
            {tasks.length === 0 ? <p className="text-sm text-muted-foreground">No tasks yet. Stage changes create them.</p>
              : <ul className="space-y-1 text-sm">{tasks.map((t) => (
                  <li key={t.id} className="flex items-center justify-between border-b py-1 last:border-0">
                    <span>{t.name}</span><Badge variant="outline" className="capitalize">{t.priority}</Badge>
                  </li>))}</ul>}
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2 mt-4">
        <Button disabled={saving || !deal.active} onClick={save}>{saving ? "Saving…" : "Save deal"}</Button>
        {deal.active && <Button variant="outline" onClick={markLost}>Mark Lost (recycle to marketing)</Button>}
        <Button variant="ghost" onClick={() => navigate("/deals")}>Back to board</Button>
      </div>
    </div>
  );
};

DealShow.path = "/deals/:id";
export default DealShow;
